import { supabase, type UserProfile } from './supabase';

/**
 * Session Validation Service
 *
 * Provides comprehensive session validation that checks:
 * 1. Supabase session exists and is valid
 * 2. Access token is not expired
 * 3. User can actually access their data in the database
 * 4. User profile is active and valid
 */

export interface SessionValidationResult {
  isValid: boolean;
  user?: UserProfile;
  error?: string;
  details: {
    hasSupabaseSession: boolean;
    isTokenValid: boolean;
    canAccessDatabase: boolean;
    userProfileValid: boolean;
    authUserId?: string;
  };
}

export const sessionValidator = {
  /**
   * Comprehensive session validation
   * This is the main method to call when checking if a user session is truly valid
   */
  async validateSession(): Promise<SessionValidationResult> {
    const result: SessionValidationResult = {
      isValid: false,
      details: {
        hasSupabaseSession: false,
        isTokenValid: false,
        canAccessDatabase: false,
        userProfileValid: false,
      },
    };

    try {
      // Step 1: Check if Supabase session exists
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        result.error = `Session error: ${sessionError.message}`;
        return result;
      }

      if (!session?.access_token || !session?.user) {
        result.error = 'No valid Supabase session found';
        return result;
      }

      result.details.hasSupabaseSession = true;
      result.details.authUserId = session.user.id;

      // Step 2: Check if token is not expired
      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      const expiresAt = session.expires_at || 0;

      if (expiresAt <= now) {
        result.error = 'Access token has expired';
        return result;
      }

      result.details.isTokenValid = true;

      // Step 3: Test database access with a simple authenticated query
      try {
        // Try to access the user's profile - this tests both authentication and RLS
        const { data: profileData, error: profileError } = await supabase
          .from('ev_user_profiles')
          .select('*')
          .eq('auth_user_id', session.user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (profileError) {
          result.error = `Database access failed: ${profileError.message}`;
          return result;
        }

        result.details.canAccessDatabase = true;

        // Step 4: Validate user profile
        if (!profileData) {
          result.error = 'User profile not found or inactive';
          return result;
        }

        // Validate profile completeness
        if (!profileData.phone || !profileData.first_name) {
          result.error = 'User profile is incomplete';
          return result;
        }

        result.details.userProfileValid = true;
        result.user = profileData;
        result.isValid = true;

        return result;
      } catch (dbError) {
        result.error = `Database query failed: ${
          dbError instanceof Error ? dbError.message : 'Unknown error'
        }`;
        return result;
      }
    } catch (error) {
      result.error = `Session validation failed: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      return result;
    }
  },

  /**
   * Quick session check - only checks if Supabase session exists
   * Useful for initial checks where you don't need full validation
   */
  async hasValidSupabaseSession(): Promise<boolean> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token || !session?.user) {
        return false;
      }

      // Check token expiry
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at || 0;

      return expiresAt > now;
    } catch {
      return false;
    }
  },

  /**
   * Validate session and return user profile if valid
   * Convenience method that combines validation and user fetching
   */
  async validateAndGetUser(): Promise<{
    user: UserProfile | null;
    error?: string;
  }> {
    const validation = await this.validateSession();

    if (validation.isValid && validation.user) {
      return { user: validation.user };
    }

    return { user: null, error: validation.error };
  },

  /**
   * Test database connectivity with a simple query
   * Useful for checking if the user has proper database access
   */
  async testDatabaseAccess(): Promise<{ success: boolean; error?: string }> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        return { success: false, error: 'No authenticated session' };
      }

      // Try a simple authenticated query
      const { error } = await supabase
        .from('ev_user_profiles')
        .select('id')
        .eq('auth_user_id', session.user.id)
        .limit(1);

      if (error) {
        return {
          success: false,
          error: `Database access failed: ${error.message}`,
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: `Database test failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },
};
